import { useEffect, useState } from "react";
import { api, type Screen, type ScreenDevice } from "../api";
import { pairingCode, relativeTime } from "../format";
import { Banner, PageHeader } from "../ui";

export function AuthorizationsPage() {
  const [screens, setScreens] = useState<Screen[] | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function load() {
    const result = await api<{ screens: Screen[] }>("/api/screens");
    setScreens(result.screens);
  }

  useEffect(() => {
    load().catch((reason: unknown) => setError(message(reason)));
    const timer = window.setInterval(() => {
      load().catch(() => undefined);
    }, 5000);
    return () => window.clearInterval(timer);
  }, []);

  const devices = (screens ?? []).flatMap((screen) =>
    screen.devices.map((device) => ({ screen, device })),
  );
  const pending = devices.filter((entry) => entry.device.status === "pending");
  const approved = devices.filter((entry) => entry.device.status === "approved");
  const blocked = devices.filter((entry) => entry.device.status === "blocked");

  return (
    <section>
      <PageHeader
        kicker="Sécurité"
        title="Autorisations"
        lede="Le code affiché sur la télé doit être le même que celui-ci. Autorisez seulement les écrans du club."
      />
      <ol className="hint">
        <li><span className="hint-n">1</span><span>La télé affiche un code à 6 chiffres.</span></li>
        <li><span className="hint-n">2</span><span>Le même code apparaît ici, avec l'adresse IP.</span></li>
        <li><span className="hint-n">3</span><span>Autorisez seulement si les deux correspondent.</span></li>
      </ol>
      {error ? <Banner tone="error">{error}</Banner> : null}
      {notice ? <Banner tone="ok">{notice}</Banner> : null}
      <h2 className="block-title">À valider {pending.length > 0 ? <span className="nav-count">{pending.length}</span> : null}</h2>
      {screens && pending.length === 0 ? (
        <p className="quiet-empty">Aucune télé en attente. Le code apparaît ici dès qu'une télé ouvre son lien.</p>
      ) : null}
      {pending.map(({ screen, device }) => (
        <DeviceRow
          key={device.id}
          screen={screen}
          device={device}
          onDone={async (text) => {
            setNotice(text);
            await load();
          }}
          onError={setError}
        />
      ))}
      {approved.length > 0 ? <h2 className="block-title">Déjà autorisées</h2> : null}
      {approved.map(({ screen, device }) => (
        <DeviceRow
          key={device.id}
          screen={screen}
          device={device}
          onDone={async (text) => {
            setNotice(text);
            await load();
          }}
          onError={setError}
        />
      ))}
      {blocked.length > 0 ? <h2 className="block-title">Refusées</h2> : null}
      {blocked.map(({ screen, device }) => (
        <DeviceRow
          key={device.id}
          screen={screen}
          device={device}
          onDone={async (text) => {
            setNotice(text);
            await load();
          }}
          onError={setError}
        />
      ))}
    </section>
  );
}

export function DeviceRow({
  screen,
  device,
  onDone,
  onError,
}: {
  screen: Screen;
  device: ScreenDevice;
  onDone: (text: string) => Promise<void>;
  onError: (text: string) => void;
}) {
  const waiting = device.status === "pending";
  return (
    <article className={waiting ? "device pending" : "device"}>
      <div>
        <p className={waiting ? "auth-code" : "auth-code compact"}>{pairingCode(device.code)}</p>
        <p className="device-name">
          {screen.name}
          {screen.location ? ` · ${screen.location}` : ""}
        </p>
        <div className="device-meta">
          <span className="meta-chip">{device.ip ?? "IP inconnue"}</span>
          <span className="meta-chip">{device.agentLabel}</span>
          <span className="meta-chip">{relativeTime(device.lastSeen)}</span>
        </div>
      </div>
      <div className="row-actions">
        {device.status !== "approved" ? (
          <button type="button" className="primary" onClick={() => void decide(screen, device, "approved", onDone, onError)}>
            Autoriser
          </button>
        ) : (
          <span className="pill online"><span className="dot" />Autorisée</span>
        )}
        {device.status !== "blocked" ? (
          <button type="button" onClick={() => void decide(screen, device, "blocked", onDone, onError)}>
            {device.status === "approved" ? "Retirer l'accès" : "Refuser"}
          </button>
        ) : (
          <>
            <span className="pill offline"><span className="dot" />Refusée</span>
            <button type="button" className="danger" onClick={() => void forget(screen, device, onDone, onError)}>
              Supprimer
            </button>
          </>
        )}
      </div>
    </article>
  );
}

async function decide(
  screen: Screen,
  device: ScreenDevice,
  status: "approved" | "blocked",
  onDone: (text: string) => Promise<void>,
  onError: (text: string) => void,
) {
  if (status === "blocked") {
    const where = device.ip ?? "IP inconnue";
    const question = device.status === "approved"
      ? `Retirer l'accès de ${screen.name} (${where}) ?`
      : `Refuser ${screen.name} (${where}) ?`;
    if (!window.confirm(question)) return;
  }
  try {
    await api(`/api/screens/${screen.id}/devices/${device.id}`, {
      method: "POST",
      body: JSON.stringify({ status }),
    });
    await onDone(status === "approved" ? `${screen.name} peut lire.` : `Accès retiré pour ${screen.name}.`);
  } catch (reason) {
    onError(message(reason));
  }
}

async function forget(
  screen: Screen,
  device: ScreenDevice,
  onDone: (text: string) => Promise<void>,
  onError: (text: string) => void,
) {
  const where = device.ip ?? "IP inconnue";
  if (!window.confirm(`Supprimer l'autorisation refusée de ${screen.name} (${where}) ? Si la télé est encore ouverte, elle redemandera un code.`)) return;
  try {
    await api(`/api/screens/${screen.id}/devices/${device.id}`, { method: "DELETE" });
    await onDone(`Autorisation supprimée pour ${screen.name}.`);
  } catch (reason) {
    onError(message(reason));
  }
}

function message(reason: unknown): string {
  return reason instanceof Error ? reason.message : "Erreur inattendue";
}
